import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import type { ILojaConfig, IMelhorEnvioConfig } from "interfaces/loja";
import type { IPedidoDetalhe } from "interfaces/loja/pedido";
import type { IEnvioPedido } from "interfaces/loja/envio";
import type { IMelhorEnvioServico } from "interfaces/loja/freteMelhorEnvio";
import {
  getLojaConfig,
  getMelhorEnvioConfig,
  getMelhorEnvioServicos,
} from "services/lojaConfig";
import {
  atualizarEnvioPedidoLoja,
  criarEnvioPedidoLoja,
  gerarEtiquetaPedidoLoja,
  marcarEntreguePedidoLoja,
  marcarPostadoPedidoLoja,
} from "services/lojaPedidos";
import {
  checkoutMelhorEnvio,
  gerarEtiquetasMelhorEnvio,
  imprimirEtiquetasMelhorEnvio,
  inserirCarrinhoMelhorEnvio,
} from "services/lojaFrete";
import {
  buildMelhorEnvioCartPayload,
  extractMelhorEnvioCartOrderId,
  extractMelhorEnvioPrintUrl,
  resolveMelhorEnvioServiceId,
} from "utils/melhorEnvioCartPayload";
import { messageFromApiError } from "utils/pedidoApiResponse";
import {
  buildPedidoOperacionalInput,
  resolvePedidoOperacional,
} from "utils/pedidoOperacional";
import { useMaterialUIController, setMessage } from "context";
import PedidoExpedicaoResumo from "./PedidoExpedicaoResumo";
import EnvioHistoricoLogistico from "./EnvioHistoricoLogistico";
import MelhorEnvioSaldoCard from "components/MelhorEnvioSaldoCard";
import { cpfApplyMask, phoneApplyMask } from "helpers/utils";

type PedidoExpedicaoOperacionalProps = {
  pedidoId: string;
  detalhe: IPedidoDetalhe | null;
  onAfterAction: (patch?: Partial<IPedidoDetalhe>) => Promise<void>;
};

function pickPedidoPatchFromApi(data: unknown): Partial<IPedidoDetalhe> {
  if (!data || typeof data !== "object") return {};
  const o = data as Record<string, unknown>;
  const keys = [
    "status",
    "statusLogistico",
    "statusSeparacao",
    "codigoRastreio",
    "provedorFrete",
    "servicoFrete",
    "tipoEntrega",
  ] as const;
  const out: Partial<IPedidoDetalhe> = {};
  for (const k of keys) {
    if (k in o) (out as Record<string, unknown>)[k] = o[k];
  }
  return out;
}

function envioPrincipal(d: IPedidoDetalhe): IEnvioPedido | undefined {
  return d.envios?.[0];
}

/** UUID retornado ao adicionar o frete ao carrinho no Melhor Envio. */
function isUuidMelhorEnvio(s: string): boolean {
  const t = s.trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    t
  );
}

const ME_CHECKOUT_STORAGE_PREFIX = "tnv_me_checkout_ok_";

function readMeCheckoutDone(pedidoId: string): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    return (
      sessionStorage.getItem(`${ME_CHECKOUT_STORAGE_PREFIX}${pedidoId}`) === "1"
    );
  } catch {
    return false;
  }
}

function persistMeCheckoutDone(pedidoId: string) {
  try {
    sessionStorage.setItem(`${ME_CHECKOUT_STORAGE_PREFIX}${pedidoId}`, "1");
  } catch {
    /* ignore quota / private mode */
  }
}

/** Após etiqueta (ou envio), o passo “pagar no ME” deixa de fazer sentido na UI. */
function statusLogisticoIndicaPosPagamentoMe(sl: string | undefined): boolean {
  const s = String(sl ?? "");
  return (
    s === "ETIQUETA_GERADA" ||
    s === "POSTADO" ||
    s === "EM_TRANSITO" ||
    s === "ENTREGUE"
  );
}

export default function PedidoExpedicaoOperacional({
  pedidoId,
  detalhe,
  onAfterAction,
}: PedidoExpedicaoOperacionalProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);
  const [controller, dispatch] = useMaterialUIController();

  const input = useMemo(
    () =>
      detalhe
        ? buildPedidoOperacionalInput(
            detalhe as unknown as Parameters<
              typeof buildPedidoOperacionalInput
            >[0],
            detalhe.pagamentos ?? []
          )
        : null,
    [detalhe]
  );

  const o = useMemo(
    () => (input ? resolvePedidoOperacional(input) : null),
    [input]
  );

  const env = detalhe ? envioPrincipal(detalhe) : undefined;

  /** ID do envio no Melhor Envio — comprado no carrinho ME e salvo no envio. */
  const melhorEnvioOrderId = useMemo(
    () => (env?.melhorEnvioOrderId ?? "").trim(),
    [env?.melhorEnvioOrderId]
  );

  const provedorExibicao = env?.provedor ?? detalhe?.provedorFrete;
  const servicoExibicao = env?.servico ?? detalhe?.servicoFrete;
  const rastreioExibicao = env?.rastreio ?? detalhe?.codigoRastreio;

  const [provedor, setProvedor] = useState("");
  const [servico, setServico] = useState("");
  const [rastreio, setRastreio] = useState("");
  const [meOrderIdInput, setMeOrderIdInput] = useState("");
  const [meServiceIdInput, setMeServiceIdInput] = useState("");
  const [meServiceIdTouched, setMeServiceIdTouched] = useState(false);
  const [docDestinatario, setDocDestinatario] = useState("");
  const [telDestinatario, setTelDestinatario] = useState("");
  const [emailDestinatario, setEmailDestinatario] = useState("");
  const [cartCtx, setCartCtx] = useState<{
    me: IMelhorEnvioConfig | null;
    loja: ILojaConfig | null;
    servicos: IMelhorEnvioServico[];
  }>({ me: null, loja: null, servicos: [] });
  const [submitting, setSubmitting] = useState<
    "checkout" | "etiqueta" | "imprimir" | "postar" | "entregar" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** Checkout ME concluído neste browser (a API ME não persiste flag no pedido até a etiqueta). */
  const [meCheckoutFeito, setMeCheckoutFeito] = useState(() =>
    readMeCheckoutDone(pedidoId)
  );

  useEffect(() => {
    setMeCheckoutFeito(readMeCheckoutDone(pedidoId));
  }, [pedidoId]);

  useEffect(() => {
    setProvedor((provedorExibicao ?? "").trim());
    setServico((servicoExibicao ?? "").trim());
    setRastreio((rastreioExibicao ?? "").trim());
  }, [provedorExibicao, servicoExibicao, rastreioExibicao]);

  useEffect(() => {
    setMeOrderIdInput((env?.melhorEnvioOrderId ?? "").trim());
  }, [env?.id, env?.melhorEnvioOrderId]);

  /** Pré-preenche documento do destinatário com o CPF do comprador (API pedido), se ainda vazio. */
  useEffect(() => {
    const digits = String(detalhe?.compradorCpf ?? "").replace(/\D/g, "");
    if (digits.length < 11) return;
    setDocDestinatario((prev) => (prev.trim() ? prev : digits));
  }, [detalhe?.id, detalhe?.compradorCpf]);

  /** E-mail: sessão do painel primeiro; senão e-mail do comprador no pedido. */
  useEffect(() => {
    const fromSessao = String(controller.user?.email ?? "").trim();
    const fromPedido = String(detalhe?.compradorEmail ?? "").trim();
    const email = fromSessao || fromPedido;
    if (!email) return;
    setEmailDestinatario((prev) => (prev.trim() ? prev : email));
  }, [detalhe?.id, detalhe?.compradorEmail, controller.user?.email]);

  /**
   * Telefone do comprador (cadastro atleta ligado ao utilizador do pedido).
   * O perfil admin em `users` não tem telefone — não há outra fonte na sessão.
   */
  useEffect(() => {
    const digits = String(detalhe?.compradorTelefone ?? "").replace(/\D/g, "");
    if (digits.length < 10) return;
    setTelDestinatario((prev) => (prev.trim() ? prev : digits));
  }, [detalhe?.id, detalhe?.compradorTelefone]);

  useEffect(() => {
    if (!detalhe?.id) return;
    let alive = true;
    (async () => {
      try {
        const meRes = await getMelhorEnvioConfig();
        let loja: ILojaConfig | null = null;
        try {
          const r = await getLojaConfig();
          if (r.status === 200 && r.data) loja = r.data;
        } catch {
          loja = null;
        }
        let servicos: IMelhorEnvioServico[] = [];
        if (meRes.data?.id) {
          try {
            const s = await getMelhorEnvioServicos();
            if (s.status === 200 && Array.isArray(s.data)) servicos = s.data;
          } catch {
            servicos = [];
          }
        }
        if (!alive) return;
        setCartCtx({ me: meRes.data, loja, servicos });
      } catch {
        if (alive) setCartCtx({ me: null, loja: null, servicos: [] });
      }
    })();
    return () => {
      alive = false;
    };
  }, [detalhe?.id]);

  useEffect(() => {
    if (meServiceIdTouched) return;
    if (!cartCtx.me || !cartCtx.servicos.length || !detalhe) return;
    const id = resolveMelhorEnvioServiceId(
      cartCtx.servicos,
      detalhe.servicoFrete,
      cartCtx.me.servicos
    );
    if (id != null) setMeServiceIdInput(String(id));
  }, [cartCtx, detalhe, meServiceIdTouched]);

  const meIdResolvido = useMemo(() => {
    const local = meOrderIdInput.trim();
    if (local) return local;
    return melhorEnvioOrderId;
  }, [meOrderIdInput, melhorEnvioOrderId]);

  const entregaConcluida = useMemo(() => {
    if (!o || !detalhe) return false;
    return (
      o.statusLogistico === "ENTREGUE" || String(detalhe.status) === "ENTREGUE"
    );
  }, [o, detalhe]);

  const podeGerarEtiqueta =
    o &&
    o.statusSeparacao === "SEPARADO" &&
    (o.statusLogistico === "PENDENTE" ||
      o.statusLogistico === "AGUARDANDO_ENVIO");

  const podeMarcarPostado =
    o &&
    o.statusSeparacao === "SEPARADO" &&
    (o.statusLogistico === "AGUARDANDO_ENVIO" ||
      o.statusLogistico === "ETIQUETA_GERADA");

  const podeMarcarEntregue =
    o &&
    (o.statusLogistico === "POSTADO" || o.statusLogistico === "EM_TRANSITO");

  const serviceIdNumerico = useMemo(() => {
    const n = parseInt(meServiceIdInput.trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [meServiceIdInput]);

  const podeInserirCarrinhoMe = Boolean(
    podeGerarEtiqueta &&
    !entregaConcluida &&
    cartCtx.me?.ativo &&
    detalhe?.enderecoEntrega &&
    serviceIdNumerico != null
  );

  /** Base técnica (UUID + separado); a UI do botão de impressão exige etiqueta já gerada. */
  const podeImprimirEtiquetaMe = Boolean(
    o &&
    o.statusSeparacao === "SEPARADO" &&
    !entregaConcluida &&
    isUuidMelhorEnvio(meIdResolvido.trim())
  );

  const statusLogisticoComEtiquetaMe = useMemo(() => {
    const s = o?.statusLogistico ?? "";
    return (
      s === "ETIQUETA_GERADA" ||
      s === "POSTADO" ||
      s === "EM_TRANSITO" ||
      s === "ENTREGUE"
    );
  }, [o?.statusLogistico]);

  const exibirAbrirImpressaoMe = Boolean(
    podeImprimirEtiquetaMe && statusLogisticoComEtiquetaMe
  );

  const uuidMeOk = useMemo(
    () => isUuidMelhorEnvio(meIdResolvido.trim()),
    [meIdResolvido]
  );

  const freteMelhorEnvioPagoOuAvançado = useMemo(
    () =>
      meCheckoutFeito ||
      Boolean(o && statusLogisticoIndicaPosPagamentoMe(o.statusLogistico)),
    [meCheckoutFeito, o]
  );

  /**
   * Um único fluxo: inserir no carrinho ME (se ainda não houver UUID) e em seguida checkout.
   * Exibe enquanto o frete ME ainda não foi pago/não avançou.
   */
  const mostrarBotaoPagarFreteMelhorEnvio = Boolean(
    podeGerarEtiqueta &&
    !freteMelhorEnvioPagoOuAvançado &&
    (uuidMeOk || podeInserirCarrinhoMe)
  );

  /** Só depois que o botão único de pagar frete ME deixar de aparecer. */
  const exibirGerarEtiquetaEImpressaoMe = !mostrarBotaoPagarFreteMelhorEnvio;

  const runAction = useCallback(
    async (
      kind: "etiqueta" | "postar" | "entregar",
      fn: () => Promise<{ data: unknown; status: number }>
    ) => {
      setError(null);
      setSuccess(null);
      setSubmitting(kind);
      try {
        const { data, status } = await fn();
        const body = data as {
          success?: boolean;
          message?: string;
          data?: unknown;
        };
        if (status === 200 && body.success === true) {
          const patch = pickPedidoPatchFromApi(body.data);
          await onAfterAction(patch);
          const msg =
            body.message ||
            (kind === "etiqueta"
              ? "Etiqueta registrada."
              : kind === "postar"
                ? "Marcado como postado."
                : "Marcado como entregue.");
          setSuccess(msg);
          setMessage(dispatch, { open: true, severity: "success", text: msg });
        } else {
          const msg = messageFromApiError(
            data,
            "Não foi possível concluir a ação."
          );
          setError(msg);
          setMessage(dispatch, { open: true, severity: "error", text: msg });
        }
      } catch (e: unknown) {
        const text =
          e instanceof Error
            ? e.message
            : "Falha na comunicação com o servidor.";
        setError(text);
        setMessage(dispatch, { open: true, severity: "error", text });
      } finally {
        setSubmitting(null);
      }
    },
    [onAfterAction, dispatch]
  );

  /**
   * 1) Se houver `melhorEnvioOrderId`: gera a etiqueta na API do Melhor Envio (POST /shipment/generate).
   * 2) Registra no pedido (status ETIQUETA_GERADA + dados do envio).
   * Sem ID ME: apenas o passo 2, modo manual (outro transportador).
   */
  const onEtiqueta = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setSubmitting("etiqueta");
    try {
      if (meIdResolvido) {
        try {
          const meRes = await gerarEtiquetasMelhorEnvio([meIdResolvido]);
          if (meRes.status !== 200) {
            const msg = messageFromApiError(
              meRes.data,
              "Falha ao gerar etiqueta no Melhor Envio."
            );
            setError(msg);
            setMessage(dispatch, { open: true, severity: "error", text: msg });
            return;
          }
        } catch (e: unknown) {
          const ax = e as { response?: { data?: unknown } };
          const msg = messageFromApiError(
            ax.response?.data,
            "Falha ao gerar etiqueta no Melhor Envio."
          );
          setError(msg);
          setMessage(dispatch, { open: true, severity: "error", text: msg });
          return;
        }
      }

      const { data, status } = await gerarEtiquetaPedidoLoja(pedidoId, {
        provedor:
          provedor.trim() || (meIdResolvido ? "Melhor Envio" : undefined),
        servico: servico.trim() || undefined,
        rastreio: rastreio.trim() || undefined,
        melhorEnvioOrderId: meIdResolvido || undefined,
      });
      const body = data as {
        success?: boolean;
        message?: string;
        data?: unknown;
      };
      if (status === 200 && body.success === true) {
        const patch = pickPedidoPatchFromApi(body.data);
        await onAfterAction(patch);
        const msg =
          body.message ||
          (meIdResolvido
            ? "Etiqueta gerada no Melhor Envio e registrada no pedido."
            : "Etiqueta registrada.");
        setSuccess(msg);
        setMessage(dispatch, { open: true, severity: "success", text: msg });
      } else {
        const msg = messageFromApiError(
          data,
          "Não foi possível concluir a ação."
        );
        setError(msg);
        setMessage(dispatch, { open: true, severity: "error", text: msg });
      }
    } catch (e: unknown) {
      const text =
        e instanceof Error ? e.message : "Falha na comunicação com o servidor.";
      setError(text);
      setMessage(dispatch, { open: true, severity: "error", text });
    } finally {
      setSubmitting(null);
    }
  }, [
    pedidoId,
    provedor,
    servico,
    rastreio,
    meIdResolvido,
    onAfterAction,
    dispatch,
  ]);

  /** Insere no carrinho ME e persiste o UUID no pedido. Retorna `envioId` para o checkout usar (evita closure obsoleta). */
  const inserirCarrinhoMelhorEnvioCore = useCallback(async (): Promise<{
    orderId: string;
    envioId?: string;
  }> => {
    if (!detalhe || !cartCtx.me || serviceIdNumerico == null) {
      throw new Error(
        "Configure o Melhor Envio (ativo), o ID do serviço e o endereço de entrega do pedido."
      );
    }
    const built = buildMelhorEnvioCartPayload({
      pedido: detalhe,
      meConfig: cartCtx.me,
      loja: cartCtx.loja,
      serviceId: serviceIdNumerico,
      documentoDestinatario: docDestinatario.trim() || undefined,
      telefoneDestinatario: telDestinatario.trim() || undefined,
      emailDestinatario: emailDestinatario.trim() || undefined,
    });
    if (built.ok === false) {
      throw new Error(built.error);
    }

    const cartRes = await inserirCarrinhoMelhorEnvio(built.body);
    if (cartRes.status !== 200) {
      throw new Error(
        messageFromApiError(
          cartRes.data,
          "Falha ao inserir o frete no carrinho do Melhor Envio."
        )
      );
    }

    const orderId = extractMelhorEnvioCartOrderId(cartRes.data);
    if (!orderId) {
      throw new Error(
        "Carrinho ME respondeu sem UUID de envio. Verifique o retorno da API ou informe o ID manualmente."
      );
    }

    let envioId: string | undefined;

    if (env?.id) {
      const up = await atualizarEnvioPedidoLoja(env.id, {
        melhorEnvioOrderId: orderId,
      });
      if (up.status !== 200) {
        throw new Error(
          messageFromApiError(
            up.data,
            "Envio criado no ME, mas não foi possível salvar o ID no pedido."
          )
        );
      }
      envioId = env.id;
    } else {
      const cr = await criarEnvioPedidoLoja(pedidoId, {
        tipo: "TRANSPORTADORA",
        servico: servico.trim() || detalhe?.servicoFrete || undefined,
        melhorEnvioOrderId: orderId,
      });
      if (cr.status !== 200 && cr.status !== 201) {
        throw new Error(
          messageFromApiError(
            cr.data,
            "Envio criado no ME, mas não foi possível criar o registro de envio no pedido."
          )
        );
      }
      const created = cr.data as { id?: string } | undefined;
      if (typeof created?.id === "string") envioId = created.id;
    }

    return { orderId, envioId };
  }, [
    detalhe,
    cartCtx.me,
    cartCtx.loja,
    serviceIdNumerico,
    docDestinatario,
    telDestinatario,
    emailDestinatario,
    env?.id,
    pedidoId,
    servico,
  ]);

  const checkoutMelhorEnvioCore = useCallback(
    async (
      orderId: string,
      envioIdPreferido?: string | null
    ): Promise<void> => {
      if (!isUuidMelhorEnvio(orderId)) {
        throw new Error(
          "UUID do envio no Melhor Envio inválido. Verifique o ID no campo ou refaça o fluxo."
        );
      }

      const meRes = await checkoutMelhorEnvio([orderId]);
      if (meRes.status !== 200) {
        throw new Error(
          messageFromApiError(
            meRes.data,
            "Falha ao pagar o frete no Melhor Envio (checkout)."
          )
        );
      }

      const patchId = envioIdPreferido ?? env?.id ?? null;
      if (patchId) {
        const up = await atualizarEnvioPedidoLoja(patchId, {
          melhorEnvioOrderId: orderId,
        });
        if (up.status !== 200) {
          throw new Error(
            messageFromApiError(
              up.data,
              "Checkout OK, mas não foi possível salvar o ID no envio."
            )
          );
        }
      } else {
        const cr = await criarEnvioPedidoLoja(pedidoId, {
          tipo: "TRANSPORTADORA",
          servico: servico.trim() || detalhe?.servicoFrete || undefined,
          melhorEnvioOrderId: orderId,
        });
        if (cr.status !== 200 && cr.status !== 201) {
          throw new Error(
            messageFromApiError(
              cr.data,
              "Checkout OK, mas não foi possível criar o envio com o ID ME."
            )
          );
        }
      }

      await onAfterAction();
    },
    [env?.id, pedidoId, servico, detalhe?.servicoFrete, onAfterAction]
  );

  /** Carrinho ME (quando preciso) + checkout carteira ME em uma ação só. */
  const onPagarFreteMelhorEnvio = useCallback(async () => {
    let orderUuid = meIdResolvido.trim();

    setError(null);
    setSuccess(null);

    if (!isUuidMelhorEnvio(orderUuid) && !podeInserirCarrinhoMe) {
      const msg =
        "Configure o Melhor Envio (ativo), o ID do serviço e o endereço de entrega do pedido.";
      setError(msg);
      setMessage(dispatch, { open: true, severity: "error", text: msg });
      return;
    }

    setSubmitting("checkout");

    try {
      let envioIdParaCheckout: string | undefined = env?.id;

      if (!isUuidMelhorEnvio(orderUuid)) {
        const { orderId, envioId } = await inserirCarrinhoMelhorEnvioCore();
        orderUuid = orderId;
        envioIdParaCheckout = envioId ?? envioIdParaCheckout;
        setMeOrderIdInput(orderId);
      }

      await checkoutMelhorEnvioCore(orderUuid, envioIdParaCheckout);

      persistMeCheckoutDone(pedidoId);
      setMeCheckoutFeito(true);

      const ok =
        "Frete pago no Melhor Envio (saldo da carteira). Agora você pode gerar a etiqueta.";
      setSuccess(ok);
      setMessage(dispatch, { open: true, severity: "success", text: ok });
    } catch (e: unknown) {
      const text =
        e instanceof Error ? e.message : "Falha na comunicação com o servidor.";
      setError(text);
      setMessage(dispatch, { open: true, severity: "error", text });
    } finally {
      setSubmitting(null);
    }
  }, [
    meIdResolvido,
    podeInserirCarrinhoMe,
    inserirCarrinhoMelhorEnvioCore,
    checkoutMelhorEnvioCore,
    env?.id,
    pedidoId,
    dispatch,
  ]);

  const onImprimirEtiquetaMelhorEnvio = useCallback(async () => {
    const id = meIdResolvido.trim();
    if (!isUuidMelhorEnvio(id)) {
      const msg =
        "Informe o UUID do envio no Melhor Envio para solicitar a página de impressão.";
      setError(msg);
      setMessage(dispatch, { open: true, severity: "error", text: msg });
      return;
    }
    setError(null);
    setSuccess(null);
    setSubmitting("imprimir");
    try {
      const meRes = await imprimirEtiquetasMelhorEnvio({
        orders: [id],
        mode: "private",
      });
      if (meRes.status !== 200) {
        const msg = messageFromApiError(
          meRes.data,
          "Falha ao obter URL de impressão no Melhor Envio."
        );
        setError(msg);
        setMessage(dispatch, { open: true, severity: "error", text: msg });
        return;
      }
      const url = extractMelhorEnvioPrintUrl(meRes.data);
      if (!url) {
        const msg =
          "A API não retornou uma URL de impressão reconhecida. Abra o painel do Melhor Envio ou tente novamente.";
        setError(msg);
        setMessage(dispatch, { open: true, severity: "warning", text: msg });
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      const ok = "Página de impressão do Melhor Envio aberta em nova aba.";
      setSuccess(ok);
      setMessage(dispatch, { open: true, severity: "success", text: ok });
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      const msg = messageFromApiError(
        ax.response?.data,
        "Falha ao solicitar impressão no Melhor Envio."
      );
      setError(msg);
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    } finally {
      setSubmitting(null);
    }
  }, [meIdResolvido, dispatch]);

  const onPostar = useCallback(() => {
    runAction("postar", () => marcarPostadoPedidoLoja(pedidoId));
  }, [pedidoId, runAction]);

  const onEntregar = useCallback(() => {
    runAction("entregar", () => marcarEntreguePedidoLoja(pedidoId));
  }, [pedidoId, runAction]);

  if (!detalhe || !o) return null;

  return (
    <Stack spacing={2}>
      {entregaConcluida && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
        >
          <Typography variant="body2" fontWeight={600}>
            Entrega concluída
          </Typography>
          <Typography variant="body2" color="text.secondary">
            O pedido foi marcado como entregue. As ações de expedição estão
            encerradas.
          </Typography>
        </Alert>
      )}

      {o.statusSeparacao !== "SEPARADO" && !entregaConcluida && (
        <Alert severity="warning">
          Conclua a separação antes de operar a expedição.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {cartCtx.me &&
        !cartCtx.me.ativo &&
        podeGerarEtiqueta &&
        !entregaConcluida && (
          <Alert severity="warning" variant="outlined">
            Melhor Envio está inativo nas configurações. Ative em{" "}
            <strong>Configurações → Frete Melhor Envio</strong> para usar o
            carrinho integrado.
          </Alert>
        )}

      {cartCtx.me?.id &&
        !cartCtx.servicos.length &&
        podeGerarEtiqueta &&
        !entregaConcluida && (
          <Alert severity="warning" variant="outlined">
            Não foi possível carregar a lista de serviços do ME. Verifique o
            token e salve novamente a configuração do Melhor Envio, ou informe o{" "}
            <strong>ID do serviço</strong> manualmente (número retornado na
            cotação).
          </Alert>
        )}

      <PedidoExpedicaoResumo
        statusLogistico={o.statusLogistico}
        provedor={provedorExibicao}
        servico={servicoExibicao}
        rastreio={rastreioExibicao}
        enviadoEm={env?.enviadoEm}
        entregueEm={env?.entregueEm}
        entregaConcluida={entregaConcluida}
      />

      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
          opacity: entregaConcluida ? 0.85 : 1,
        }}
      >
        <MDBox p={2.5}>
          <Typography variant="button" fontWeight={700}>
            Ações
          </Typography>
          {entregaConcluida ? (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Nenhuma ação disponível — fluxo logístico finalizado.
            </Typography>
          ) : (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
                mb={2}
              >
                Fluxo típico ME: <strong>Pagar frete Melhor Envio</strong>{" "}
                (carrinho + carteira) → <strong>Gerar etiqueta</strong> →{" "}
                <strong>Abrir impressão ME</strong> →{" "}
                <strong>Marcar postado</strong> /{" "}
                <strong>Marcar entregue</strong>.
              </Typography>
              <MDBox
                display="grid"
                sx={
                  !!submitting || !podeGerarEtiqueta
                    ? {
                        backgroundColor: theme.palette.grey[200],
                        padding: theme.spacing(2),
                        borderRadius: theme.spacing(2),
                      }
                    : {}
                }
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                }}
                gap={2}
                mb={2}
              >
                <MDInput
                  label="ID do serviço ME (cotação)"
                  value={meServiceIdInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setMeServiceIdTouched(true);
                    setMeServiceIdInput(e.target.value);
                  }}
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                  helperText="Preenchido automaticamente quando possível; ajuste se necessário."
                />
                <MDInput
                  label="CPF/CNPJ destinatário (recomendado)"
                  value={cpfApplyMask(docDestinatario ?? "")}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDocDestinatario(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                />
                <MDInput
                  label="Telefone destinatário"
                  value={phoneApplyMask(telDestinatario ?? "")}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setTelDestinatario(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                  helperText="Pré-preenchido com o celular do comprador (cadastro de atleta), quando existir."
                />
                <MDInput
                  label="E-mail destinatário"
                  value={emailDestinatario}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setEmailDestinatario(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                  helperText="Pré-preenchido com o e-mail da sua sessão no painel; se não houver, usa o e-mail do comprador."
                />
                <MDInput
                  label="ID do envio Melhor Envio (UUID)"
                  value={meOrderIdInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setMeOrderIdInput(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                  placeholder="ex.: 550e8400-e29b-41d4-a716-446655440000"
                  helperText="Preenchido automaticamente ao pagar o frete (botão abaixo) ou cole do painel ME."
                  sx={{
                    gridColumn: {
                      xs: "1 / -1",
                      sm: "1 / -1",
                      md: "1 / -1",
                    },
                  }}
                />
                <MDInput
                  label="Provedor"
                  value={provedor}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setProvedor(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                />
                <MDInput
                  label="Serviço"
                  value={servico}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setServico(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                />
                <MDInput
                  label="Rastreio"
                  value={rastreio}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setRastreio(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeGerarEtiqueta}
                  sx={{
                    gridColumn: {
                      xs: "1 / -1",
                      sm: "1 / -1",
                      md: "span 2",
                    },
                  }}
                />
              </MDBox>
              <Stack spacing={2} mt={0.5}>
                {mostrarBotaoPagarFreteMelhorEnvio && (
                  <Box>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: 0.6 }}
                    >
                      Melhor Envio — pagamento do frete
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      useFlexGap
                      gap={1}
                      mt={0.75}
                    >
                      <MDButton
                        variant="contained"
                        color="info"
                        disabled={
                          !!submitting ||
                          entregaConcluida ||
                          (!uuidMeOk
                            ? !podeInserirCarrinhoMe
                            : !podeGerarEtiqueta)
                        }
                        onClick={onPagarFreteMelhorEnvio}
                      >
                        {submitting === "checkout"
                          ? "Processando…"
                          : "Pagar frete Melhor Envio"}
                      </MDButton>
                    </Stack>
                  </Box>
                )}

                {mostrarBotaoPagarFreteMelhorEnvio && <Divider />}

                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 0.6 }}
                  >
                    Etiqueta, impressão e conclusão
                  </Typography>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    gap={1}
                    mt={0.75}
                  >
                    {/* Só após carrinho ME e pagamento ME (quando esses botões existirem na UI). */}
                    {exibirGerarEtiquetaEImpressaoMe && (
                      <>
                        <MDButton
                          variant="gradient"
                          color="dark"
                          disabled={
                            !!submitting ||
                            !podeGerarEtiqueta ||
                            entregaConcluida
                          }
                          onClick={onEtiqueta}
                        >
                          {submitting === "etiqueta"
                            ? "Processando…"
                            : "Gerar etiqueta"}
                        </MDButton>
                        {exibirAbrirImpressaoMe && (
                          <MDButton
                            variant="outlined"
                            color="info"
                            disabled={
                              !!submitting ||
                              !podeImprimirEtiquetaMe ||
                              entregaConcluida
                            }
                            onClick={onImprimirEtiquetaMelhorEnvio}
                          >
                            {submitting === "imprimir"
                              ? "Abrindo…"
                              : "Abrir impressão ME"}
                          </MDButton>
                        )}
                      </>
                    )}
                    <MDButton
                      variant="outlined"
                      color="dark"
                      disabled={
                        !!submitting || !podeMarcarPostado || entregaConcluida
                      }
                      onClick={onPostar}
                    >
                      {submitting === "postar"
                        ? "Processando…"
                        : "Marcar postado"}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="success"
                      disabled={
                        !!submitting || !podeMarcarEntregue || entregaConcluida
                      }
                      onClick={onEntregar}
                    >
                      {submitting === "entregar"
                        ? "Processando…"
                        : "Marcar entregue"}
                    </MDButton>
                  </Stack>
                </Box>
              </Stack>
            </>
          )}
        </MDBox>
      </Card>

      {/* Saldo do melhor envio */}
      <MelhorEnvioSaldoCard />

      <EnvioHistoricoLogistico envios={detalhe.envios ?? []} />
    </Stack>
  );
}
